/* RESPONSIBLE TEAM: team-workflows */

import AdminSummary, { type AdminSummaryWireFormat } from './admin-summary';

export interface OperatorWorkflowEventDataWireFormat {
  admin?: AdminSummaryWireFormat;
  wait_started?: WaitStartedWireFormat;
  skip_assignment_action?: SkipAssignmentActionWireFormat;
  workflow_finished?: WorkflowFinishedWireFormat;
  action_data?: ActionDataWireFormat;
  language_override_updated?: LanguageOverrideUpdatedWireFormat;
  user_inactivity_conversation_autoclosed?: UserInactivityConversationAutoClosedWireFormat;
  ai_agent_ignoring_user_message?: AiAgentIgnoringUserMessageWireFormat;
  ai_agent_workflow_started?: AiAgentWorkflowStartedWireFormat;
  ai_agent_workflow_stopped?: AiAgentWorkflowStoppedWireFormat;
  ai_agent_workflow_finished?: AiAgentWorkflowFinishedWireFormat;
  ai_agent_workflow_failed?: AiAgentWorkflowFailedWireFormat;
  ai_agent_task_started?: AiAgentTaskStartedWireFormat;
  ai_agent_task_finished?: AiAgentTaskFinishedWireFormat;
  ai_agent_task_failed?: AiAgentTaskFailedWireFormat;
  ai_agent_task_paused?: AiAgentTaskPausedWireFormat;
  ai_agent_task_step_reasoning?: AiAgentTaskStepReasoningWireFormat;
  ai_agent_conditional_evaluation?: AiAgentConditionalEvaluation;
  ai_agent_content_retrieval?: AiAgentContentRetrieval;
  ai_agent_skipped_steps?: AiAgentSkippedStepsWireFormat;
  ai_agent_code_condition_error?: AiAgentCodeConditionError;
  ai_agent_set_attributes?: AiAgentSetAttributesWireFormat;
  ai_agent_task_stopped?: AiAgentTaskStoppedWireFormat;
  email_verification_successful?: EmailVerificationSuccessfulWireFormat;
  email_verification_failed?: EmailVerificationFailedWireFormat;
  email_already_verified?: EmailAlreadyVerifiedWireFormat;
  email_triggered_for_verification?: EmailTriggeredForVerificationWireFormat;
  impersonated_user_data_update_notice?: ImpersonatedUserDataUpdateNoticeWireFormat;
  ai_agent_goto_section?: AiAgentGotoSectionWireFormat;
  ai_agent_end_section?: AiAgentEndSectionWireFormat;
}

interface WaitStarted {
  durationType: string;
  customDuration: number | null;
}
type WaitStartedWireFormat = KeysToSnakeCase<WaitStarted>;

interface SkipAssignmentAction {
  outboundId: number | undefined;
  outboundType: number;
  outboundName: string;
}
type SkipAssignmentActionWireFormat = KeysToSnakeCase<SkipAssignmentAction>;

interface WorkflowFinished {
  reason: string;
}
type WorkflowFinishedWireFormat = KeysToSnakeCase<WorkflowFinished>;

interface ActionData {
  tagId?: string;
  attributeIdentifier?: string;
  value?: string;
}
type ActionDataWireFormat = KeysToSnakeCase<ActionData>;
interface LanguageOverrideUpdated {
  previousValue: string | null;
  newValue: string;
}
type LanguageOverrideUpdatedWireFormat = KeysToSnakeCase<LanguageOverrideUpdated>;

interface UserInactivityConversationAutoClosed {
  endUserInactivityTimer: number;
  identifier: string;
}
type UserInactivityConversationAutoClosedWireFormat =
  KeysToSnakeCase<UserInactivityConversationAutoClosed>;

interface AiAgentIgnoringUserMessage {
  reason: string;
}
type AiAgentIgnoringUserMessageWireFormat = KeysToSnakeCase<AiAgentIgnoringUserMessage>;

interface AiAgentWorkflowStarted {
  title: string;
  id?: number;
  rulesetId: number;
  reasoning?: string;
  workflowRelevanceScore?: number;
}

type AiAgentWorkflowStartedWireFormat = KeysToSnakeCase<AiAgentWorkflowStarted>;

interface AiAgentWorkflowFinished {
  title: string;
  id?: number;
  rulesetId: number;
}

type AiAgentWorkflowFinishedWireFormat = KeysToSnakeCase<AiAgentWorkflowFinished>;

interface AiAgentWorkflowFailed {
  title: string;
  id?: number;
  rulesetId: number;
}

type AiAgentWorkflowFailedWireFormat = KeysToSnakeCase<AiAgentWorkflowFailed>;

interface AiAgentWorkflowStopped {
  title: string;
  id?: number;
  rulesetId: number;
}

type AiAgentWorkflowStoppedWireFormat = KeysToSnakeCase<AiAgentWorkflowStopped>;

interface AiAgentTaskStarted {
  title: string;
}
type AiAgentTaskStartedWireFormat = KeysToSnakeCase<AiAgentTaskStarted>;

interface AiAgentTaskFinished {
  title: string;
}
type AiAgentTaskFinishedWireFormat = KeysToSnakeCase<AiAgentTaskFinished>;

interface AiAgentTaskFailed {
  title: string;
  reason?: string;
}
type AiAgentTaskFailedWireFormat = KeysToSnakeCase<AiAgentTaskFailed>;

interface AiAgentTaskStepReasoning {
  step_number: string;
  step_description: string;
  reason: string;
  reasoned_step_ids: { section_name: string; step_id: string }[];
}
type AiAgentTaskStepReasoningWireFormat = AiAgentTaskStepReasoning;

interface AiAgentConditionalEvaluation {
  step_number: string;
  selected_branch_number: number;
  skipped_condition: boolean;
}
type AiAgentConditionalEvaluationWireFormat = AiAgentConditionalEvaluation;

interface RetrievedContent {
  entity_type_id: number;
  entity_id: number;
  title: string;
  url: string;
  matched: boolean;
}

interface AiAgentContentRetrieval {
  step_number: string;
  query: string;
  document_count: number;
  passage_count: number;
  retrieved_content: RetrievedContent[];
}
type AiAgentContentRetrievalWireFormat = AiAgentContentRetrieval;

interface AiAgentSkippedSteps {
  skipped_steps: { section_name: string; step_number: string }[];
}
type AiAgentSkippedStepsWireFormat = AiAgentSkippedSteps;

interface AiAgentCodeConditionError {
  step_number: string;
  condition: string;
  error_message: string;
}
type AiAgentCodeConditionErrorWireFormat = AiAgentCodeConditionError;

interface AiAgentTaskStopped {
  title: string;
  reason?: string;
}
type AiAgentTaskStoppedWireFormat = KeysToSnakeCase<AiAgentTaskStopped>;

interface AiAgentTaskPaused {
  title: string;
  reason: string;
}
type AiAgentTaskPausedWireFormat = KeysToSnakeCase<AiAgentTaskPaused>;

interface AiAgentSetAttribute {
  name: string;
  value: string;
}
type AiAgentSetAttributesWireFormat = AiAgentSetAttribute[];

interface EmailVerificationSuccessful {
  title: string;
}
type EmailVerificationSuccessfulWireFormat = KeysToSnakeCase<EmailVerificationSuccessful>;

interface EmailVerificationFailed {
  title: string;
}
type EmailVerificationFailedWireFormat = KeysToSnakeCase<EmailVerificationFailed>;

interface EmailAlreadyVerified {
  title: string;
}
type EmailAlreadyVerifiedWireFormat = KeysToSnakeCase<EmailAlreadyVerified>;

interface EmailTriggeredForVerification {
  title: string;
  actionId: number;
}
type EmailTriggeredForVerificationWireFormat = KeysToSnakeCase<EmailTriggeredForVerification>;

interface ImpersonatedUserDataUpdateNotice {
  userDisplayName: string;
}
type ImpersonatedUserDataUpdateNoticeWireFormat = KeysToSnakeCase<ImpersonatedUserDataUpdateNotice>;

interface AiAgentGotoSection {
  sectionId: string;
  sectionName?: string;
  fromStepId?: string;
}
type AiAgentGotoSectionWireFormat = KeysToSnakeCase<AiAgentGotoSection>;

interface AiAgentEndSection {
  sectionId: string;
  sectionName?: string;
  returnedToSectionId?: string;
  returnedToSectionName?: string;
}
type AiAgentEndSectionWireFormat = KeysToSnakeCase<AiAgentEndSection>;

export default class OperatorWorkflowEventData {
  readonly admin?: AdminSummary;
  readonly waitStarted?: WaitStarted;
  readonly skipAssignmentAction?: SkipAssignmentAction;
  readonly workflowFinished?: WorkflowFinished;
  readonly actionData?: ActionData;
  readonly userInactivityConversationAutoClosed?: UserInactivityConversationAutoClosed;
  readonly languageOverrideUpdated?: LanguageOverrideUpdated;
  readonly aiAgentIgnoringUserMessage?: AiAgentIgnoringUserMessage;
  readonly aiAgentWorkflowStarted?: AiAgentWorkflowStarted;
  readonly aiAgentWorkflowStopped?: AiAgentWorkflowStopped;
  readonly aiAgentWorkflowFinished?: AiAgentWorkflowFinished;
  readonly aiAgentWorkflowFailed?: AiAgentWorkflowFailed;
  readonly aiAgentTaskStarted?: AiAgentTaskStarted;
  readonly aiAgentTaskFinished?: AiAgentTaskFinished;
  readonly aiAgentTaskFailed?: AiAgentTaskFailed;
  readonly aiAgentTaskStopped?: AiAgentTaskStopped;
  readonly aiAgentTaskPaused?: AiAgentTaskPaused;
  readonly aiAgentSetAttributes?: AiAgentSetAttributesWireFormat;
  readonly aiAgentTaskStepReasoning?: AiAgentTaskStepReasoningWireFormat;
  readonly aiAgentConditionalEvaluation?: AiAgentConditionalEvaluationWireFormat;
  readonly aiAgentContentRetrieval?: AiAgentContentRetrievalWireFormat;
  readonly aiAgentSkippedSteps?: AiAgentSkippedStepsWireFormat;
  readonly aiAgentCodeConditionError?: AiAgentCodeConditionErrorWireFormat;
  readonly emailVerificationSuccessful?: EmailVerificationSuccessful;
  readonly emailVerificationFailed?: EmailVerificationFailed;
  readonly emailAlreadyVerified?: EmailAlreadyVerified;
  readonly emailTriggeredForVerification?: EmailTriggeredForVerification;
  readonly impersonatedUserDataUpdateNotice?: ImpersonatedUserDataUpdateNotice;
  readonly aiAgentGotoSection?: AiAgentGotoSection;
  readonly aiAgentEndSection?: AiAgentEndSection;
  constructor(
    admin?: AdminSummary,
    waitStarted?: WaitStarted,
    skipAssignmentAction?: SkipAssignmentAction,
    workflowFinished?: WorkflowFinished,
    actionData?: ActionData,
    languageOverrideUpdated?: LanguageOverrideUpdated,
    userInactivityConversationAutoClosed?: UserInactivityConversationAutoClosed,
    aiAgentIgnoringUserMessage?: AiAgentIgnoringUserMessage,
    aiAgentWorkflowStarted?: AiAgentWorkflowStarted,
    aiAgentTaskStarted?: AiAgentTaskStarted,
    aiAgentTaskFinished?: AiAgentTaskFinished,
    aiAgentTaskFailed?: AiAgentTaskFailed,
    aiAgentTaskStopped?: AiAgentTaskStopped,
    aiAgentTaskPaused?: AiAgentTaskPaused,
    aiAgentSetAttributes?: AiAgentSetAttribute[],
    aiAgentTaskStepReasoning?: AiAgentTaskStepReasoning,
    aiAgentConditionalEvaluation?: AiAgentConditionalEvaluation,
    aiAgentContentRetrieval?: AiAgentContentRetrieval,
    aiAgentSkippedSteps?: AiAgentSkippedSteps,
    aiAgentCodeConditionError?: AiAgentCodeConditionError,
    emailVerificationSuccessful?: EmailVerificationSuccessful,
    emailVerificationFailed?: EmailVerificationFailed,
    emailAlreadyVerified?: EmailAlreadyVerified,
    emailTriggeredForVerification?: EmailTriggeredForVerification,
    aiAgentWorkflowFinished?: AiAgentWorkflowFinished,
    aiAgentWorkflowFailed?: AiAgentWorkflowFailed,
    aiAgentWorkflowStopped?: AiAgentWorkflowStopped,
    impersonatedUserDataUpdateNotice?: ImpersonatedUserDataUpdateNotice,
    aiAgentGotoSection?: AiAgentGotoSection,
    aiAgentEndSection?: AiAgentEndSection,
  ) {
    this.admin = admin;
    this.waitStarted = waitStarted;
    this.skipAssignmentAction = skipAssignmentAction;
    this.workflowFinished = workflowFinished;
    this.actionData = actionData;
    this.languageOverrideUpdated = languageOverrideUpdated;
    this.userInactivityConversationAutoClosed = userInactivityConversationAutoClosed;
    this.aiAgentIgnoringUserMessage = aiAgentIgnoringUserMessage;
    this.aiAgentWorkflowStarted = aiAgentWorkflowStarted;
    this.aiAgentWorkflowStopped = aiAgentWorkflowStopped;
    this.aiAgentWorkflowFinished = aiAgentWorkflowFinished;
    this.aiAgentWorkflowFailed = aiAgentWorkflowFailed;
    this.aiAgentTaskStarted = aiAgentTaskStarted;
    this.aiAgentTaskFinished = aiAgentTaskFinished;
    this.aiAgentTaskFailed = aiAgentTaskFailed;
    this.aiAgentTaskStopped = aiAgentTaskStopped;
    this.aiAgentTaskPaused = aiAgentTaskPaused;
    this.aiAgentSetAttributes = aiAgentSetAttributes;
    this.aiAgentTaskStepReasoning = aiAgentTaskStepReasoning;
    this.aiAgentConditionalEvaluation = aiAgentConditionalEvaluation;
    this.aiAgentContentRetrieval = aiAgentContentRetrieval;
    this.aiAgentSkippedSteps = aiAgentSkippedSteps;
    this.aiAgentCodeConditionError = aiAgentCodeConditionError;
    this.emailVerificationSuccessful = emailVerificationSuccessful;
    this.emailVerificationFailed = emailVerificationFailed;
    this.emailAlreadyVerified = emailAlreadyVerified;
    this.emailTriggeredForVerification = emailTriggeredForVerification;
    this.impersonatedUserDataUpdateNotice = impersonatedUserDataUpdateNotice;
    this.aiAgentGotoSection = aiAgentGotoSection;
    this.aiAgentEndSection = aiAgentEndSection;
  }

  static deserialize(json: OperatorWorkflowEventDataWireFormat): OperatorWorkflowEventData {
    return new OperatorWorkflowEventData(
      json.admin ? AdminSummary.deserialize(json.admin) : undefined,
      json.wait_started
        ? {
            durationType: json.wait_started.duration_type,
            customDuration: json.wait_started.custom_duration,
          }
        : undefined,
      json.skip_assignment_action
        ? {
            outboundId: json.skip_assignment_action.outbound_id,
            outboundType: json.skip_assignment_action.outbound_type,
            outboundName: json.skip_assignment_action.outbound_name,
          }
        : undefined,
      json.workflow_finished
        ? {
            reason: json.workflow_finished.reason,
          }
        : undefined,
      json.action_data
        ? {
            tagId: json.action_data.tag_id,
            attributeIdentifier: json.action_data.attribute_identifier,
            value: json.action_data.value,
          }
        : undefined,
      json.language_override_updated
        ? {
            previousValue: json.language_override_updated.previous_value,
            newValue: json.language_override_updated.new_value,
          }
        : undefined,
      json.user_inactivity_conversation_autoclosed
        ? {
            endUserInactivityTimer:
              json.user_inactivity_conversation_autoclosed.end_user_inactivity_timer,
            identifier: json.user_inactivity_conversation_autoclosed.identifier,
          }
        : undefined,
      json.ai_agent_ignoring_user_message
        ? {
            reason: json.ai_agent_ignoring_user_message.reason,
          }
        : undefined,
      json.ai_agent_workflow_started
        ? (() => {
            let value: AiAgentWorkflowStarted = {
              title: json.ai_agent_workflow_started.title,
              id: json.ai_agent_workflow_started.id,
              rulesetId: json.ai_agent_workflow_started.ruleset_id,
            };
            if (json.ai_agent_workflow_started.reasoning !== undefined) {
              value.reasoning = json.ai_agent_workflow_started.reasoning;
            }
            if (json.ai_agent_workflow_started.workflow_relevance_score !== undefined) {
              value.workflowRelevanceScore =
                json.ai_agent_workflow_started.workflow_relevance_score;
            }
            return value;
          })()
        : undefined,
      json.ai_agent_task_started ? { title: json.ai_agent_task_started.title } : undefined,
      json.ai_agent_task_finished ? { title: json.ai_agent_task_finished.title } : undefined,
      json.ai_agent_task_failed
        ? {
            title: json.ai_agent_task_failed.title,
            reason: json.ai_agent_task_failed.reason,
          }
        : undefined,
      json.ai_agent_task_stopped
        ? { title: json.ai_agent_task_stopped.title, reason: json.ai_agent_task_stopped.reason }
        : undefined,
      json.ai_agent_task_paused
        ? { title: json.ai_agent_task_paused.title, reason: json.ai_agent_task_paused.reason }
        : undefined,
      json.ai_agent_set_attributes ? json.ai_agent_set_attributes : undefined,
      json.ai_agent_task_step_reasoning ? json.ai_agent_task_step_reasoning : undefined,
      json.ai_agent_conditional_evaluation ? json.ai_agent_conditional_evaluation : undefined,
      json.ai_agent_content_retrieval ? json.ai_agent_content_retrieval : undefined,
      json.ai_agent_skipped_steps ? json.ai_agent_skipped_steps : undefined,
      json.ai_agent_code_condition_error ? json.ai_agent_code_condition_error : undefined,
      json.email_verification_successful
        ? { title: json.email_verification_successful.title }
        : undefined,
      json.email_verification_failed ? { title: json.email_verification_failed.title } : undefined,
      json.email_already_verified ? { title: json.email_already_verified.title } : undefined,
      json.email_triggered_for_verification
        ? {
            title: json.email_triggered_for_verification.title,
            actionId: json.email_triggered_for_verification.action_id,
          }
        : undefined,
      json.ai_agent_workflow_finished
        ? {
            title: json.ai_agent_workflow_finished.title,
            id: json.ai_agent_workflow_finished.id,
            rulesetId: json.ai_agent_workflow_finished.ruleset_id,
          }
        : undefined,
      json.ai_agent_workflow_failed
        ? {
            title: json.ai_agent_workflow_failed.title,
            id: json.ai_agent_workflow_failed.id,
            rulesetId: json.ai_agent_workflow_failed.ruleset_id,
          }
        : undefined,
      json.ai_agent_workflow_stopped
        ? {
            title: json.ai_agent_workflow_stopped.title,
            id: json.ai_agent_workflow_stopped.id,
            rulesetId: json.ai_agent_workflow_stopped.ruleset_id,
          }
        : undefined,
      json.impersonated_user_data_update_notice
        ? { userDisplayName: json.impersonated_user_data_update_notice.user_display_name }
        : undefined,
      json.ai_agent_goto_section
        ? {
            sectionId: json.ai_agent_goto_section.section_id,
            sectionName: json.ai_agent_goto_section.section_name,
            fromStepId: json.ai_agent_goto_section.from_step_id,
          }
        : undefined,
      json.ai_agent_end_section
        ? {
            sectionId: json.ai_agent_end_section.section_id,
            sectionName: json.ai_agent_end_section.section_name,
            returnedToSectionId: json.ai_agent_end_section.returned_to_section_id,
            returnedToSectionName: json.ai_agent_end_section.returned_to_section_name,
          }
        : undefined,
    );
  }
}
